import React, { useState, useMemo } from 'react';
import CalcShell, { StepsPanel } from '../CalcShell';
import { fmt, PieChart, AmortTable, buildAmortization, COMPOUND_OPTIONS, PAYBACK_OPTIONS, effectiveAnnual } from './_finHelpers';
import { CNetActions } from '../other/_cnet';

// Loan Calculator — 3 sub-calculators, each with its own Calculate + Clear.
// Results appear only after clicking Calculate; Clear resets inputs and hides result.
export default function LoanCalc() {
  return (
    <CalcShell showActions={false} look="cnet" title="Loan Calculator" subtitle="Amortized loan, deferred payment loan, and bond calculators."
      about={<>
        <p>A <b>loan</b> is money that a borrower receives from a lender, which must be repaid — usually with interest — over an agreed period of time.</p>
        <p>This page includes three loan calculators for the three most common repayment structures: <b>amortized</b> (equal periodic payments), <b>deferred payment</b> (lump sum at maturity), and <b>bond</b> (predetermined face value at maturity).</p>
      </>}
    >
      <AmortizedLoan />
      <hr className="my-8 border-slate-200" />
      <DeferredLoan />
      <hr className="my-8 border-slate-200" />
      <BondLoan />
    </CalcShell>
  );
}

function CompoundSelect({ value, onChange, testid }) {
  return (
    <select data-testid={testid} value={value} onChange={e => onChange(e.target.value)}>
      {COMPOUND_OPTIONS.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
    </select>
  );
}
function PaybackSelect({ value, onChange }) {
  return (
    <select value={value} onChange={e => onChange(e.target.value)}>
      {PAYBACK_OPTIONS.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
    </select>
  );
}
function periodicRateFromCompoundKey(annualPct, compoundKey, paymentPpy) {
  const eff = effectiveAnnual(annualPct, compoundKey);
  return Math.pow(1 + eff, 1 / paymentPpy) - 1;
}

const AMORT_DEFAULTS = { amount: 100000, years: 10, months: 0, rate: 6, compound: 'monthly', payback: 'every-month' };

function AmortizedLoan() {
  const [amount, setAmount] = useState(AMORT_DEFAULTS.amount);
  const [years, setYears] = useState(AMORT_DEFAULTS.years);
  const [months, setMonths] = useState(AMORT_DEFAULTS.months);
  const [rate, setRate] = useState(AMORT_DEFAULTS.rate);
  const [compound, setCompound] = useState(AMORT_DEFAULTS.compound);
  const [payback, setPayback] = useState(AMORT_DEFAULTS.payback);
  const [showSchedule, setShowSchedule] = useState(false);
  // Show computed result on initial render but hide after Clear
  const [showResult, setShowResult] = useState(true);

  const paybackOpt = PAYBACK_OPTIONS.find(o => o.key === payback) || PAYBACK_OPTIONS[4];
  const paybackPpy = paybackOpt.ppy;

  const c = useMemo(() => {
    const totalYears = years + months / 12;
    const n = Math.max(1, Math.round(totalYears * paybackPpy));
    const rPer = periodicRateFromCompoundKey(rate, compound, paybackPpy);
    const M = rPer === 0 ? amount / n : (amount * rPer * Math.pow(1 + rPer, n)) / (Math.pow(1 + rPer, n) - 1);
    return { M, n, total: M * n, interest: M * n - amount, rPer };
  }, [amount, years, months, rate, compound, payback, paybackPpy]);

  const schedule = useMemo(() => {
    return buildAmortization(amount, effectiveAnnual(rate, compound) * 100, Math.max(1, (years * 12 + months)));
  }, [amount, years, months, rate, compound]);

  const clear = () => {
    setAmount(AMORT_DEFAULTS.amount); setYears(AMORT_DEFAULTS.years); setMonths(AMORT_DEFAULTS.months);
    setRate(AMORT_DEFAULTS.rate); setCompound(AMORT_DEFAULTS.compound); setPayback(AMORT_DEFAULTS.payback);
    setShowResult(false); setShowSchedule(false);
  };
  const calc = () => setShowResult(true);

  return (
    <div>
      <h3 className="text-lg font-bold text-slate-900 mb-1">Amortized Loan: Paying Back a Fixed Amount Periodically</h3>
      <p className="text-sm text-slate-600 mb-4">Use this calculator for basic calculations of common loan types such as mortgages, auto loans, student loans, or personal loans.</p>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <div className="field"><label>Loan Amount</label><div className="flex gap-2"><span className="self-center text-slate-500">$</span><input type="number" value={amount} onChange={e => setAmount(+e.target.value || 0)} className="flex-1" data-testid="loan-amort-amount" /></div></div>
          <div className="field"><label>Loan Term</label>
            <div className="grid grid-cols-2 gap-2">
              <div className="flex gap-2"><input type="number" value={years} onChange={e => setYears(+e.target.value || 0)} /><span className="self-center text-slate-500">years</span></div>
              <div className="flex gap-2"><input type="number" value={months} onChange={e => setMonths(+e.target.value || 0)} /><span className="self-center text-slate-500">months</span></div>
            </div>
          </div>
          <div className="field"><label>Interest Rate</label><div className="flex gap-2"><input type="number" step="0.01" value={rate} onChange={e => setRate(+e.target.value || 0)} className="flex-1" /><span className="self-center text-slate-500">%</span></div></div>
          <div className="field"><label>Compound</label><CompoundSelect value={compound} onChange={setCompound} /></div>
          <div className="field"><label>Pay Back</label><PaybackSelect value={payback} onChange={setPayback} /></div>
          <CNetActions onCalc={calc} onClear={clear} testId="loan-amort" />
        </div>
        <div>
          {showResult ? (
            <>
              <div className="result-panel mb-4"><div className="result-title">Payment {paybackOpt.label}</div><div className="result-value" data-testid="loan-amort-payment">{fmt(c.M)}</div></div>
              <table className="data-table"><tbody>
                <tr><td>Total of {c.n} Payments</td><td className="text-right font-semibold">{fmt(c.total)}</td></tr>
                <tr><td>Total Interest</td><td className="text-right font-semibold text-orange-600">{fmt(c.interest)}</td></tr>
              </tbody></table>
              <div className="mt-4"><PieChart parts={[{ label: 'Principal', value: amount, color: '#2563eb' }, { label: 'Interest', value: c.interest, color: '#10b981' }]} size={160} /></div>
              <button onClick={() => setShowSchedule(v => !v)} data-testid="loan-view-schedule"
                className="mt-4 text-blue-700 hover:text-blue-900 text-sm font-semibold underline">
                {showSchedule ? '▲ Hide' : '▼ View'} Amortization Schedule Table
              </button>
            </>
          ) : (
            <div className="text-center text-slate-500 py-8 italic">Click <b>Calculate</b> to see the payment schedule.</div>
          )}
        </div>
      </div>

      {showResult && showSchedule && (
        <div className="mt-6">
          <h4 className="font-bold text-slate-900 mb-3">Amortization Schedule</h4>
          <AmortTable schedule={schedule} />
        </div>
      )}

      {showResult && (
        <StepsPanel steps={[
          `Effective annual rate = ${(effectiveAnnual(rate, compound) * 100).toFixed(4)}% (from ${rate}% compounded ${compound})`,
          `Per-period rate = ${(c.rPer * 100).toFixed(4)}% (${paybackOpt.label})`,
          `Number of payments n = ${c.n}`,
          `M = P·r(1+r)ⁿ / ((1+r)ⁿ − 1) = <b>${fmt(c.M)}</b>`,
        ]} />
      )}
    </div>
  );
}

const DEFER_DEFAULTS = { amount: 100000, years: 10, months: 0, rate: 6, compound: 'annually' };
function DeferredLoan() {
  const [amount, setAmount] = useState(DEFER_DEFAULTS.amount);
  const [years, setYears] = useState(DEFER_DEFAULTS.years);
  const [months, setMonths] = useState(DEFER_DEFAULTS.months);
  const [rate, setRate] = useState(DEFER_DEFAULTS.rate);
  const [compound, setCompound] = useState(DEFER_DEFAULTS.compound);
  const [showResult, setShowResult] = useState(true);
  const [showSchedule, setShowSchedule] = useState(false);
  const c = useMemo(() => {
    const t = years + months / 12;
    const eff = effectiveAnnual(rate, compound);
    const fv = amount * Math.pow(1 + eff, t);
    // Build annual schedule showing balance growth
    const yInt = Math.max(1, Math.ceil(t));
    const rows = [];
    let bal = amount;
    for (let y = 1; y <= yInt; y++) {
      const prev = bal;
      const yearFrac = y <= t ? 1 : (t - (y - 1));
      bal = amount * Math.pow(1 + eff, Math.min(y, t));
      rows.push({ year: y, startBalance: prev, interest: bal - prev, endBalance: bal });
    }
    return { fv, interest: fv - amount, rows };
  }, [amount, years, months, rate, compound]);
  const clear = () => { setAmount(DEFER_DEFAULTS.amount); setYears(DEFER_DEFAULTS.years); setMonths(DEFER_DEFAULTS.months); setRate(DEFER_DEFAULTS.rate); setCompound(DEFER_DEFAULTS.compound); setShowResult(false); };
  const calc = () => setShowResult(true);
  return (
    <div>
      <h3 className="text-lg font-bold text-slate-900 mb-1">Deferred Payment Loan: Paying Back a Lump Sum Due at Maturity</h3>
      <p className="text-sm text-slate-600 mb-4">Compute a single payoff at loan maturity for a deferred payment loan.</p>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <div className="field"><label>Loan Amount</label><div className="flex gap-2"><span className="self-center text-slate-500">$</span><input type="number" value={amount} onChange={e => setAmount(+e.target.value || 0)} className="flex-1" /></div></div>
          <div className="field"><label>Loan Term</label>
            <div className="grid grid-cols-2 gap-2">
              <div className="flex gap-2"><input type="number" value={years} onChange={e => setYears(+e.target.value || 0)} /><span className="self-center text-slate-500">years</span></div>
              <div className="flex gap-2"><input type="number" value={months} onChange={e => setMonths(+e.target.value || 0)} /><span className="self-center text-slate-500">months</span></div>
            </div>
          </div>
          <div className="field"><label>Interest Rate</label><div className="flex gap-2"><input type="number" step="0.01" value={rate} onChange={e => setRate(+e.target.value || 0)} className="flex-1" /><span className="self-center text-slate-500">%</span></div></div>
          <div className="field"><label>Compound</label><CompoundSelect value={compound} onChange={setCompound} /></div>
          <CNetActions onCalc={calc} onClear={clear} testId="loan-defer" />
        </div>
        <div>
          {showResult ? (
            <>
              <div className="result-panel mb-4"><div className="result-title">Amount Due at Loan Maturity</div><div className="result-value" data-testid="loan-deferred-fv">{fmt(c.fv)}</div></div>
              <table className="data-table"><tbody><tr><td>Total Interest</td><td className="text-right font-semibold text-orange-600">{fmt(c.interest)}</td></tr></tbody></table>
              <div className="mt-4"><PieChart parts={[{ label: 'Principal', value: amount, color: '#2563eb' }, { label: 'Interest', value: c.interest, color: '#10b981' }]} size={160} /></div>
              <button onClick={() => setShowSchedule(v => !v)} data-testid="loan-defer-view-schedule"
                className="mt-4 text-blue-700 hover:text-blue-900 text-sm font-semibold underline">
                {showSchedule ? '▲ Hide' : '▼ View'} Schedule Table
              </button>
            </>
          ) : (<div className="text-center text-slate-500 py-8 italic">Click <b>Calculate</b> to see the maturity payoff.</div>)}
        </div>
      </div>
      {showResult && showSchedule && (
        <div className="mt-4 border border-slate-200 rounded overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-blue-800 text-white"><tr><th className="px-3 py-2 text-left">Year</th><th className="px-3 py-2 text-right">Starting Balance</th><th className="px-3 py-2 text-right">Interest</th><th className="px-3 py-2 text-right">Ending Balance</th></tr></thead>
            <tbody>{c.rows.map((r,i)=>(<tr key={i} className={i%2?'bg-slate-50':''}><td className="px-3 py-2">{r.year}</td><td className="px-3 py-2 text-right">{fmt(r.startBalance)}</td><td className="px-3 py-2 text-right text-orange-600">{fmt(r.interest)}</td><td className="px-3 py-2 text-right font-semibold">{fmt(r.endBalance)}</td></tr>))}</tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const BOND_DEFAULTS = { face: 100000, years: 10, months: 0, rate: 6, compound: 'annually' };
function BondLoan() {
  const [face, setFace] = useState(BOND_DEFAULTS.face);
  const [years, setYears] = useState(BOND_DEFAULTS.years);
  const [months, setMonths] = useState(BOND_DEFAULTS.months);
  const [rate, setRate] = useState(BOND_DEFAULTS.rate);
  const [compound, setCompound] = useState(BOND_DEFAULTS.compound);
  const [showResult, setShowResult] = useState(true);
  const [showSchedule, setShowSchedule] = useState(false);
  const c = useMemo(() => {
    const t = years + months / 12;
    const eff = effectiveAnnual(rate, compound);
    const pv = face / Math.pow(1 + eff, t);
    // Build annual accrual schedule from pv up to face
    const yInt = Math.max(1, Math.ceil(t));
    const rows = [];
    let bal = pv;
    for (let y = 1; y <= yInt; y++) {
      const prev = bal;
      bal = pv * Math.pow(1 + eff, Math.min(y, t));
      rows.push({ year: y, startBalance: prev, interest: bal - prev, endBalance: bal });
    }
    return { pv, interest: face - pv, rows };
  }, [face, years, months, rate, compound]);
  const clear = () => { setFace(BOND_DEFAULTS.face); setYears(BOND_DEFAULTS.years); setMonths(BOND_DEFAULTS.months); setRate(BOND_DEFAULTS.rate); setCompound(BOND_DEFAULTS.compound); setShowResult(false); };
  const calc = () => setShowResult(true);
  return (
    <div>
      <h3 className="text-lg font-bold text-slate-900 mb-1">Bond: Paying Back a Predetermined Amount Due at Loan Maturity</h3>
      <p className="text-sm text-slate-600 mb-4">Compute the initial value of a bond/loan based on a predetermined face value at maturity.</p>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <div className="field"><label>Predetermined Due Amount</label><div className="flex gap-2"><span className="self-center text-slate-500">$</span><input type="number" value={face} onChange={e => setFace(+e.target.value || 0)} className="flex-1" /></div></div>
          <div className="field"><label>Loan Term</label>
            <div className="grid grid-cols-2 gap-2">
              <div className="flex gap-2"><input type="number" value={years} onChange={e => setYears(+e.target.value || 0)} /><span className="self-center text-slate-500">years</span></div>
              <div className="flex gap-2"><input type="number" value={months} onChange={e => setMonths(+e.target.value || 0)} /><span className="self-center text-slate-500">months</span></div>
            </div>
          </div>
          <div className="field"><label>Interest Rate</label><div className="flex gap-2"><input type="number" step="0.01" value={rate} onChange={e => setRate(+e.target.value || 0)} className="flex-1" /><span className="self-center text-slate-500">%</span></div></div>
          <div className="field"><label>Compound</label><CompoundSelect value={compound} onChange={setCompound} /></div>
          <CNetActions onCalc={calc} onClear={clear} testId="loan-bond" />
        </div>
        <div>
          {showResult ? (
            <>
              <div className="result-panel mb-4"><div className="result-title">Amount Received When the Loan Starts</div><div className="result-value" data-testid="loan-bond-pv">{fmt(c.pv)}</div></div>
              <table className="data-table"><tbody><tr><td>Total Interest</td><td className="text-right font-semibold text-orange-600">{fmt(c.interest)}</td></tr></tbody></table>
              <div className="mt-4"><PieChart parts={[{ label: 'Principal', value: c.pv, color: '#2563eb' }, { label: 'Interest', value: c.interest, color: '#10b981' }]} size={160} /></div>
              <button onClick={() => setShowSchedule(v => !v)} data-testid="loan-bond-view-schedule"
                className="mt-4 text-blue-700 hover:text-blue-900 text-sm font-semibold underline">
                {showSchedule ? '▲ Hide' : '▼ View'} Schedule Table
              </button>
            </>
          ) : (<div className="text-center text-slate-500 py-8 italic">Click <b>Calculate</b> to see the initial price.</div>)}
        </div>
      </div>
      {showResult && showSchedule && (
        <div className="mt-4 border border-slate-200 rounded overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-blue-800 text-white"><tr><th className="px-3 py-2 text-left">Year</th><th className="px-3 py-2 text-right">Starting Balance</th><th className="px-3 py-2 text-right">Interest</th><th className="px-3 py-2 text-right">Ending Balance</th></tr></thead>
            <tbody>{c.rows.map((r,i)=>(<tr key={i} className={i%2?'bg-slate-50':''}><td className="px-3 py-2">{r.year}</td><td className="px-3 py-2 text-right">{fmt(r.startBalance)}</td><td className="px-3 py-2 text-right text-orange-600">{fmt(r.interest)}</td><td className="px-3 py-2 text-right font-semibold">{fmt(r.endBalance)}</td></tr>))}</tbody>
          </table>
        </div>
      )}
    </div>
  );
}
